import { InfoOutlined } from '@mui/icons-material';
import { Alert, Button, CircularProgress, Stack, Typography } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { type UseFormReturn, useFormState } from 'react-hook-form';
import { useSelector } from 'react-redux';
import ModalV2 from '/@/components/bayer/modal-v2';
import type { PlantingMergedResult } from '/@/components/field-list-v2/components/sidebar/hooks';
import Icon from '/@/components/icon';
import useUnitProvider from '/@/core/context/unit';
import useDebounce from '/@/hooks/debounce/useDebounce';
import { useMyFields } from '/@/hooks/fields';
import { useProducts } from '/@/hooks/recommendation';
import { useReports } from '/@/hooks/reports';
import { useZones } from '/@/hooks/zones';
import { _ } from '/@/locale';
import { MuiAutocomplete } from '/@/mui/autocomplete';
import { MuiSelect } from '/@/mui/select';
import { getSessionBrands } from '/@/store/session/selectors';
import type { EVENTS as CROP_MONITORING_EVENTS } from '/@/store/tracking/events/crop-monitoring';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import type { CropType } from '/@/types/crop-monitoring';
import type { Field } from '/@/types/field';
import { getCrops } from '/@/views/dashboard/hooks/crops';
import EnvironmentInput from './components/EnvironmentInput';
import FieldviewAutofillIcon from './components/FieldviewAutofillIcon';
import PlantingDateInput from './components/PlantingDateInput';
import ProductSelector from './components/ProductSelector';
import TechnologySelector from './components/TechnologySelector';
import {
    AVAILABLE_CROPS,
    type CropMonitoringFormType,
    type FieldOption,
    MAX_ENVIRONMENT,
    MIN_ENVIRONMENT,
} from './types';
import {
    FIELDVIEW_CROP_MAP,
    findBestProductMatch,
    getEnvironmentFromSameCropMonitoring,
    getInferredPreviousCrop,
} from './utils';

const CropMonitoringFormModal = ({
    open,
    setClose,
    goBack,
    form,
    field,
    onSubmit,
    submitting,
    onlyEdit,
    entry_source,
    plantingReport,
}: {
    open: boolean;
    setClose: () => void;
    goBack?: () => void;
    form: UseFormReturn<CropMonitoringFormType>;
    field?: Field;
    onSubmit: () => void;
    submitting: boolean;
    onlyEdit: boolean;
    entry_source: Parameters<(typeof CROP_MONITORING_EVENTS)['Add Crop Flow create began']>[0]['entry_source'];
    plantingReport?: PlantingMergedResult;
}) => {
    const crop = form.watch('crop');
    const product = form.watch('product');
    const technology = form.watch('technology');
    const plantingDate = form.watch('plantingDate');
    const environment = form.watch('environment');
    const previousCrop = form.watch('previousCrop');
    const { dirtyFields } = useFormState({ control: form.control });
    const selectedFieldId = form.watch('fieldId');
    const units = useUnitProvider();
    const sessionBrands = useSelector(getSessionBrands);

    // Field search (when no field provided)
    const [fieldSearchQuery, setFieldSearchQuery] = useState('');
    const debouncedFieldSearch = useDebounce(fieldSearchQuery, 500);
    const fieldsQuery = useMyFields({
        page: 1,
        pageSize: 50,
        selectOnly: 'id,name,lat,lng,crop_monitoring',
        name: debouncedFieldSearch || undefined,
    });

    const fieldOptions = useMemo(() => {
        if (field || !fieldsQuery.data) return [];
        return fieldsQuery.data.results
            .filter((f: Field) => !f.activeCropMonitoring)
            .map((f: Field) => ({ label: f.name, value: f.id }));
    }, [field, fieldsQuery.data]);

    const selectedField = useMemo(() => {
        if (field) return field;
        if (!selectedFieldId || !fieldsQuery.data) return null;
        return fieldsQuery.data.results.find((f: Field) => f.id === selectedFieldId);
    }, [field, selectedFieldId, fieldsQuery.data]);

    const activeField = field || selectedField;

    // Crop options from zones
    const zonesQuery = useZones({
        lat: activeField?.lat,
        lng: activeField?.lng,
    });
    const cropOptions = useMemo(() => {
        const cropCatalog = getCrops();
        const cropsOptions = AVAILABLE_CROPS.filter((c) => zonesQuery.data?.some((zone) => zone.type === c))
            .map((c) => cropCatalog[c])
            .filter((c) => c !== undefined)
            .map((c) => ({ label: c.name, value: c.value }));
        return cropsOptions;
    }, [zonesQuery.data]);

    const previousCropsOptions = useMemo(() => {
        return [{ label: _('Sin Datos'), value: 'none' }, ...cropOptions, { label: _('Otros'), value: 'other' }];
    }, [cropOptions]);

    const plantingForPreviousCropQuery = useReports(
        { fieldId: activeField?.id ?? 0, type: 'planting' },
        {
            enabled: open && !!activeField && !onlyEdit && !activeField?.activeCropMonitoring,
        }
    );

    // Auto-set "Cultivo antecesor" from the next-older FieldView merged planting and/or
    // the crop monitoring with the newest planting date (whichever is more recent).
    useEffect(() => {
        if (onlyEdit || !activeField) return;
        if (dirtyFields.previousCrop) return;
        const inferred = getInferredPreviousCrop(
            activeField,
            plantingForPreviousCropQuery.data,
            plantingReport?.report
        );
        if (inferred && cropOptions.some((c) => c.value === inferred)) {
            form.setValue('previousCrop', inferred, { shouldDirty: false });
        }
    }, [
        onlyEdit,
        activeField,
        cropOptions,
        plantingForPreviousCropQuery.data,
        plantingReport?.report,
        form,
        dirtyFields.previousCrop,
    ]);

    // Auto-set environment from the latest past campaign on this field for the same crop
    useEffect(() => {
        if (onlyEdit || !activeField || !crop) return;
        if (dirtyFields.environment) return;
        const inferred = getEnvironmentFromSameCropMonitoring(activeField, crop);
        if (inferred !== undefined) {
            form.setValue('environment', inferred, { shouldDirty: false, shouldValidate: true });
            form.clearErrors('environment');
        }
    }, [onlyEdit, activeField, crop, dirtyFields.environment, form]);

    // Products
    const debouncedEnvironment = useDebounce(environment, 1000);
    const productsQuery = useProducts({
        lat: activeField?.lat?.toString() || '',
        lng: activeField?.lng?.toString() || '',
        crop: crop || 'corn',
        plantingDate: plantingDate ? dayjs(plantingDate).format('YYYY-MM-DD') : '',
        brandCodename: sessionBrands[crop || 'corn']?.[0]?.codename || '',
        environment: debouncedEnvironment?.toString() || '',
    });

    // Track which fields were actually pre-filled by FieldView
    const fvProductQuery = plantingReport?.seedProductNames[0];
    const [productFromFieldview, setProductFromFieldview] = useState(
        // For non-corn, the raw string is set immediately; for corn we wait for catalog match
        () => !!fvProductQuery && crop !== 'corn'
    );
    const [techFromFieldview, setTechFromFieldview] = useState(false);
    const handleTechAutofilled = useCallback(() => setTechFromFieldview(true), []);

    const fvCropMatches = !!plantingReport && crop === FIELDVIEW_CROP_MAP[plantingReport.cropName.toLowerCase()];
    const fvDateMatches = !!plantingReport?.plantingDate && plantingDate === plantingReport.plantingDate;

    // Autofill product from FieldView once catalog loads (corn only)
    useEffect(() => {
        if (!fvProductQuery || crop !== 'corn' || !productsQuery.data) return;
        const currentProduct = form.getValues('product');
        if (typeof currentProduct === 'number') return; // already matched to a catalog ID
        const allProducts = [
            ...(productsQuery.data.featuredProducts || []),
            ...(productsQuery.data.otherProducts || []),
        ];
        const match = findBestProductMatch(
            fvProductQuery,
            allProducts,
            (p: { product?: { catalogName?: string } }) => p.product?.catalogName || ''
        );
        if (match) {
            form.setValue('product', (match as { id: number }).id);
            setProductFromFieldview(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productsQuery.data, fvProductQuery, crop, form.getValues, form.setValue]);

    // Keep productFromFieldview in sync for non-corn crops (handles both the async form.reset
    // path and the case where the user edits the field away from the original FieldView value)
    useEffect(() => {
        if (!fvProductQuery || crop === 'corn') return;
        setProductFromFieldview(product === fvProductQuery);
    }, [fvProductQuery, crop, product]);

    // Reset product and technology when environment out of range (corn only)
    useEffect(() => {
        if (crop === 'corn') {
            if (environment && (environment < MIN_ENVIRONMENT || environment > MAX_ENVIRONMENT)) {
                form.setValue('product', undefined);
            }
        }
    }, [environment, form, crop]);

    // Handlers
    const handlePlantingDateChange = (date: string) => {
        form.setValue('plantingDate', date);
        form.clearErrors('plantingDate');
    };

    const handleEnvironmentChange = (value: number | undefined) => {
        form.setValue('environment', value, { shouldValidate: true });
        if (value) form.clearErrors('environment');
    };

    const handleProductChange = (value: number | undefined | string) => {
        form.setValue('product', value, { shouldValidate: true });
        form.clearErrors('product');
        setProductFromFieldview(false);
    };

    const handleTechnologyChange = useCallback(
        (value: number | undefined) => {
            form.setValue('technology', value, { shouldValidate: true });
            form.clearErrors('technology');
            setTechFromFieldview(false);
        },
        [form]
    );

    const [convertedMinEnv, convertedMaxEnv] = useMemo(
        () => [units.toYield(MIN_ENVIRONMENT, 'corn', 2).value, units.toYield(MAX_ENVIRONMENT, 'corn', 2).value],
        [units]
    );

    const isProductDisabled = useMemo(() => {
        if (!environment || !plantingDate) return true;
        const env = units.toYield(environment, 'corn', 2).value;
        return env < convertedMinEnv || env > convertedMaxEnv;
    }, [environment, plantingDate, units, convertedMinEnv, convertedMaxEnv]);

    const isTechnologyDisabled = isProductDisabled || !product;

    const handleSubmit = () => {
        const requiresTechnology = crop === 'corn';
        const hasField = !!(field || selectedFieldId);
        const hasValidEnvironment = !form.formState.errors.environment;

        if (!crop) form.setError('crop', { type: 'required', message: _('Campo requerido') });
        if (!hasField) form.setError('fieldId', { type: 'required', message: _('Campo requerido') });
        if (!plantingDate) form.setError('plantingDate', { type: 'required', message: _('Campo requerido') });
        if (!environment) form.setError('environment', { type: 'required', message: _('Campo requerido') });
        if (!product) form.setError('product', { type: 'required', message: _('Campo requerido') });
        if (requiresTechnology && !technology)
            form.setError('technology', { type: 'required', message: _('Campo requerido') });

        if (
            crop &&
            hasField &&
            plantingDate &&
            environment &&
            hasValidEnvironment &&
            product &&
            (!requiresTechnology || technology)
        ) {
            onSubmit();
            mpTrackEvent(onlyEdit ? 'Add Crop Flow edit completed' : 'Add Crop Flow create completed', {
                entry_source,
                crop: crop as CropType,
                planting_date: plantingDate,
                previous_crop: previousCrop,
            });
        }
    };

    return (
        <ModalV2
            open={open}
            onClose={setClose}
            goBack={goBack}
            title={onlyEdit ? _('Editar campaña') : _('Agregar próximo cultivo')}
            bodyProps={{ spacing: 3, alignItems: 'flex-start', textAlign: 'left' }}
            actions={
                <Button
                    onClick={handleSubmit}
                    variant='contained'
                    color='secondary'
                    disabled={submitting}
                    startIcon={submitting ? <CircularProgress size={20} /> : undefined}
                    data-testid='field-monitoring-creation-dialog-submit-button'
                >
                    {onlyEdit ? _('Guardar cambios') : _('Confirmar cultivo')}
                </Button>
            }
            actionsProps={{ sx: { justifyContent: 'flex-end' } }}
            maxWidth='md'
        >
            {plantingReport && (
                <Alert severity='info' icon={<Icon type='fieldview' size='xs' />} sx={{ width: '100%' }}>
                    <Stack>
                        <Typography variant='body2' fontWeight='bold' color='primary'>
                            {_('Datos de siembra de FieldView')}
                        </Typography>
                        <Typography variant='body2' color='primary'>
                            {_('Confirmá si son correctos. Podés editarlos si es necesario.')}
                        </Typography>
                    </Stack>
                </Alert>
            )}

            {!field && (
                <Stack spacing={0.5} sx={{ width: '100%' }}>
                    <Typography variant='subtitle1' color='primary' fontWeight='bold'>
                        {_('Lote')}
                    </Typography>
                    <MuiAutocomplete
                        sx={{
                            width: '100%',
                            '& .MuiInputBase-input': { color: (t: any) => t.palette.primary.main },
                        }}
                        label={<Typography color='primary'>{_('Buscar lote')}</Typography>}
                        options={fieldOptions}
                        value={selectedFieldId}
                        onChange={(_: React.SyntheticEvent, option: FieldOption | null) => {
                            form.setValue('fieldId', option?.value);
                        }}
                        onInputChange={(_: React.SyntheticEvent, value: string) => setFieldSearchQuery(value)}
                        getOptionLabel={(option: FieldOption) => option.label || ''}
                        getOptionValue={(option: FieldOption) => option.value}
                        loading={fieldsQuery.isValidating}
                        noOptionsText={_('No se encontraron lotes')}
                    />
                </Stack>
            )}

            <Grid2 container spacing={2} sx={{ width: '100%' }}>
                <Grid2 xs={6}>
                    <Stack spacing={0.5}>
                        <Typography variant='subtitle1' color='primary' fontWeight='bold'>
                            {_('Cultivo antecesor')}
                        </Typography>
                        <MuiSelect
                            options={previousCropsOptions}
                            placeholder={_('Indicá el cultivo antecesor')}
                            formControlProps={{ fullWidth: true }}
                            {...form.register('previousCrop')}
                            value={previousCrop}
                            data-testid='field-monitoring-creation-dialog-previous-crop'
                        />
                    </Stack>
                </Grid2>
                <Grid2 xs={6}>
                    <Stack spacing={0.5}>
                        <Typography variant='subtitle1' color='primary' fontWeight='bold'>
                            {_('Nuevo cultivo')}
                        </Typography>
                        <MuiSelect
                            options={cropOptions}
                            formControlProps={{ fullWidth: true }}
                            {...form.register('crop', {
                                required: true,
                                onChange: (e) => {
                                    const newCrop = e.target.value;
                                    form.setValue(
                                        'product',
                                        newCrop !== 'corn' && fvProductQuery ? fvProductQuery : undefined
                                    );
                                    form.setValue('technology', undefined);
                                    setProductFromFieldview(newCrop !== 'corn' && !!fvProductQuery);
                                    setTechFromFieldview(false);
                                },
                            })}
                            value={crop}
                            error={!!form.formState.errors.crop}
                            endAdornment={fvCropMatches ? <FieldviewAutofillIcon /> : undefined}
                            data-testid='field-monitoring-creation-dialog-crop'
                        />
                    </Stack>
                </Grid2>
            </Grid2>

            {activeField && (
                <Grid2 container spacing={2} sx={{ width: '100%' }}>
                    <Grid2 xs={6}>
                        <PlantingDateInput
                            value={plantingDate}
                            onChange={handlePlantingDateChange}
                            field={activeField}
                            crop={crop}
                            error={form.formState.errors.plantingDate?.message as string}
                            endAdornment={fvDateMatches ? <FieldviewAutofillIcon /> : undefined}
                        />
                    </Grid2>
                    <Grid2 xs={6}>
                        <EnvironmentInput
                            crop={crop || 'corn'}
                            value={environment}
                            onChange={handleEnvironmentChange}
                            units={units}
                            error={form.formState.errors.environment?.message as string}
                            minEnvironment={MIN_ENVIRONMENT}
                            maxEnvironment={MAX_ENVIRONMENT}
                        />
                    </Grid2>
                </Grid2>
            )}

            {!!plantingReport?.seedProductNames.length && (
                <Alert severity='warning' icon={<InfoOutlined fontSize='large' />} sx={{ width: '100%' }}>
                    <Typography variant='body2' fontWeight='bold' component='span'>
                        {crop === 'corn'
                            ? _('Híbrido y tecnología detectada: {{names}}.', {
                                  names: plantingReport.seedProductNames.join(', '),
                              })
                            : _('Variedad detectada: {{names}}.', {
                                  names: plantingReport.seedProductNames.join(', '),
                              })}{' '}
                    </Typography>
                    <Typography variant='body2' component='span'>
                        {_('Verificá los datos antes de continuar.')}
                    </Typography>
                </Alert>
            )}

            <Grid2 container spacing={2} sx={{ width: '100%' }}>
                <Grid2 xs={crop === 'corn' ? 6 : 12}>
                    <ProductSelector
                        crop={crop}
                        value={product}
                        onChange={handleProductChange}
                        productsQuery={productsQuery}
                        disabled={isProductDisabled}
                        units={units}
                        minEnvironment={MIN_ENVIRONMENT}
                        maxEnvironment={MAX_ENVIRONMENT}
                        environment={environment}
                        error={form.formState.errors.product?.message as string}
                        isFromFieldview={productFromFieldview}
                    />
                </Grid2>
                {crop === 'corn' && (
                    <Grid2 xs={6}>
                        <TechnologySelector
                            value={technology}
                            onChange={handleTechnologyChange}
                            disabled={isTechnologyDisabled}
                            error={form.formState.errors.technology?.message as string}
                            isFromFieldview={techFromFieldview}
                            autofillQuery={fvProductQuery}
                            onAutofilled={handleTechAutofilled}
                        />
                    </Grid2>
                )}
            </Grid2>
        </ModalV2>
    );
};

export default CropMonitoringFormModal;
